import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core';
import api from 'services/api';
import { ANAO, ASIM } from 'consts';
import { getCodEmpresa } from 'lib/functions';
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    maxWidth: 900
  },
  card: { marginTop: theme.spacing(2) },
  actions: { marginTop: theme.spacing(3) },
  message: { marginTop: theme.spacing(2) }
}));

const INITIAL_FORM = {
  enviapushcomservico: ANAO,
  urlmsgpushappcli: '',
  enviamsgpushaodispquest: ANAO,
  integwhatsapp: ANAO,
  adicionaddinumerotelefone: ANAO,
  provedorwhatsapp: 0,
  urlbasewhatsappcloudapi: '',
  phonenumberidwhatsappcloudapi: '',
  businessaccountwhatsappcloudapi: '',
  businessidwhatsappcloudapi: '',
  accesstokenwhatsappcloudapi: '',
  urllink2bot: '',
  tokenlink2bot: '',
  tokenlink2botconfigurado: false
};

const PROVEDORES = [
  'Selecione o provedor',
  'Z-API',
  'Twilio',
  'ManyChat',
  'Bradial',
  'WhatsApp Cloud API (Meta)',
  'Link2Bot (somente Web)'
];

const PROVEDOR_CLOUD_API = 5;
const PROVEDOR_LINK2BOT = 6;

const CAMPOS_CLOUD_API = [
  ['urlbasewhatsappcloudapi', 'URL base da API'],
  ['phonenumberidwhatsappcloudapi', 'Phone Number ID'],
  ['businessaccountwhatsappcloudapi', 'WhatsApp Business Account ID'],
  ['businessidwhatsappcloudapi', 'Business ID']
];

/* Limpa os tokens digitados e marca se o token do Link2Bot já está cadastrado */
const limparTokens = (values) => ({
  ...values,
  accesstokenwhatsappcloudapi: '',
  tokenlink2bot: '',
  tokenlink2botconfigurado: values.tokenlink2botconfigurado || Boolean(values.tokenlink2bot)
});

export default function ConfiguracaoGeralCRM() {
  const classes = useStyles();

  const [form, setForm] = useState(INITIAL_FORM);
  const [tab, setTab] = useState(0);
  const [original, setOriginal] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const codempresa = getCodEmpresa() || 0;
  const disabled = saving || loadFailed;
  const provedor = Number(form.provedorwhatsapp);

  /* Carrega os dados iniciais da tela */
  useEffect(() => {
    let active = true;

    api.get('/crm/configuracoes/geral', { params: { codempresa } }).then(({ data }) => {
      if (!active) return;

      const values = {
        ...INITIAL_FORM,
        ...data,
        accesstokenwhatsappcloudapi: '',
        tokenlink2bot: ''
      };

      setForm(values);
      setOriginal(values);
    }).catch((e) => {
      if (active) {
        setError(true);
        setLoadFailed(true);
        setMessage(e.response?.data?.error || 'Não foi possível carregar as configurações!');
      }
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [codempresa]);

  /* Evento onChange dos checkboxes */
  const toggle = (field) => (event, checked) => {
    const isChecked = typeof checked === 'boolean' ? checked : Boolean(event?.target?.checked);

    setMessage('');

    setForm((old) => ({
      ...old,
      [field]: isChecked ? ASIM : ANAO
    }));
  };

  /* Evento onChange da integração com o WhatsApp */
  const onChangeIntegWhatsApp = (event, checked) => {
    toggle('integwhatsapp')(event, checked);

    if (!checked) setTab(0);
  };

  /* Evento onChange dos campos de texto */
  const onChangeCampo = (field) => (event) => {
    const { value } = event.target;

    setForm((old) => ({
      ...old,
      [field]: value
    }));
  };

  /* Evento onChange do provedor */
  const onChangeProvedor = (event) => {
    const value = Number(event.target.value);

    setForm((old) => ({
      ...old,
      provedorwhatsapp: value
    }));
  };

  /* Evento onChange das abas */
  const onChangeTab = (event, value) => {
    setTab(value);
  };

  /* Clicar para salvar */
  const salvar = async () => {
    setSaving(true);
    setMessage('');

    try {
      await api.put('/crm/configuracoes/geral', {
        codempresa,
        ...form
      });

      setOriginal(limparTokens(form));
      setForm(limparTokens);

      setError(false);
      setMessage('Configurações salvas com sucesso!');
    } catch (e) {
      setError(true);
      setMessage(e.response?.data?.error || 'Não foi possível salvar as configurações.');
    } finally {
      setSaving(false);
    }
  };

  /* Clicar para cancelar */
  const onClickCancelar = () => {
    setForm({ ...original });
    setTab(0);
    setMessage('');
  };

  /* Renderiza os campos do Link2Bot */
  const renderLink2Bot = () => (
    <div>
      <Typography
        variant="subtitle1"
        style={{ marginTop: 16 }}
      >
        Link2Bot - integração exclusiva CRM Web
      </Typography>

      <TextField
        fullWidth
        margin="normal"
        variant="outlined"
        label="URL da API Link2Bot (HTTPS)"
        value={form.urllink2bot || ''}
        disabled={disabled}
        onChange={onChangeCampo('urllink2bot')}
        helperText="Informe a URL base de integração fornecida pelo Link2Bot; não inclua o identificador de uma requisição específica."
      />

      <TextField
        fullWidth
        margin="normal"
        variant="outlined"
        type="password"
        label="Novo Bearer Token"
        autoComplete="new-password"
        value={form.tokenlink2bot || ''}
        disabled={disabled}
        onChange={onChangeCampo('tokenlink2bot')}
        helperText={form.tokenlink2botconfigurado ? 'Token cadastrado. Deixe em branco para mantê-lo.' : 'Informe o token fornecido pelo Link2Bot.'}
      />

      <Typography
        variant="body2"
        color="textSecondary"
      >
        O token será enviado como Authorization: Bearer &lt;token&gt;. Esta tela configura o BSP; o envio de mensagens será integrado separadamente.
      </Typography>
    </div>
  );

  /* Renderiza os campos da WhatsApp Cloud API */
  const renderCloudApi = () => (
    <div>
      {CAMPOS_CLOUD_API.map(([field, label]) => (
        <TextField
          key={field}
          fullWidth
          margin="normal"
          variant="outlined"
          label={label}
          value={form[field] || ''}
          disabled={disabled}
          onChange={onChangeCampo(field)}
        />
      ))}

      <TextField
        fullWidth
        margin="normal"
        variant="outlined"
        type="password"
        label="Novo Access Token (deixe vazio para manter o atual)"
        autoComplete="new-password"
        value={form.accesstokenwhatsappcloudapi || ''}
        disabled={disabled}
        onChange={onChangeCampo('accesstokenwhatsappcloudapi')}
      />
    </div>
  );

  /* Renderiza os campos específicos do provedor selecionado */
  const renderProvedor = () => {
    if (provedor === PROVEDOR_LINK2BOT) return renderLink2Bot();

    if (provedor === PROVEDOR_CLOUD_API) return renderCloudApi();

    if (provedor > 0) {
      return (
        <Typography color="textSecondary">
          Provedor selecionado. As credenciais específicas já cadastradas no desktop serão preservadas; a edição dessas credenciais será disponibilizada em etapa posterior.
        </Typography>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography variant="h4">
        Configurações Gerais - CRM
      </Typography>

      <Typography color="textSecondary">
        Parâmetros de funcionamento do módulo CRM
      </Typography>

      <Card className={classes.card}>
        <CardContent>
          <Tabs
            value={tab}
            onChange={onChangeTab}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Geral" />

            <Tab
              label="Integração WhatsApp"
              disabled={form.integwhatsapp !== ASIM}
            />
            <Tab
              label="Envio mensagem push"
              disabled={form.enviapushcomservico !== ASIM}
            />
          </Tabs>

          {tab === 0 ? (
            <div style={{ paddingTop: 16 }}>
              <div>
                <FormControlLabel
                  label="Envia comunicado serviço por Mensagem Push"
                  control={
                    <Checkbox
                      color="primary"
                      checked={form.enviapushcomservico === ASIM}
                      disabled={disabled}
                      onChange={toggle('enviapushcomservico')}
                    />
                  }
                />
                <Typography variant="body2" color="textSecondary">
                  Habilita a aba de envio de mensagem push
                </Typography>
              </div>
              <FormControlLabel
                label="Envio de comunicados por WhatsApp"
                control={
                  <Checkbox
                    color="primary"
                    checked={form.integwhatsapp === ASIM}
                    disabled={disabled}
                    onChange={onChangeIntegWhatsApp}
                  />
                }
              />

              <Typography
                variant="body2"
                color="textSecondary"
              >
                Habilita a aba de integração WhatsApp
              </Typography>

              <FormControlLabel
                label="Adiciona DDI ao número de telefone"
                control={
                  <Checkbox
                    color="primary"
                    checked={form.adicionaddinumerotelefone === ASIM}
                    onChange={toggle('adicionaddinumerotelefone')}
                    disabled={disabled}
                  />
                }
              />
            </div>
          ) : tab === 2 ? (
            <div style={{ paddingTop: 20 }}>
              <TextField
                fullWidth
                label="URL mensagem push"
                value={form.urlmsgpushappcli}
                onChange={onChangeCampo('urlmsgpushappcli')}
                disabled={disabled}
                inputProps={{ maxLength: 200 }}
                variant="outlined"
                margin="normal"
              />
              <FormControlLabel
                label="Ao disponibilizar questionário para respostas envia mensagem push"
                control={
                  <Checkbox
                    color="primary"
                    checked={form.enviamsgpushaodispquest === ASIM}
                    onChange={toggle('enviamsgpushaodispquest')}
                    disabled={disabled}
                  />
                }
              />
            </div>
          ) : (
            <div style={{ paddingTop: 20 }}>
              <TextField
                select
                fullWidth
                label="Provedor (BSP)"
                value={form.provedorwhatsapp}
                disabled={disabled}
                onChange={onChangeProvedor}
                variant="outlined"
                margin="normal"
              >
                {PROVEDORES.map((nome, id) => (
                  <MenuItem
                    key={id}
                    value={id}
                  >
                    {nome}
                  </MenuItem>
                ))}
              </TextField>

              {renderProvedor()}
            </div>
          )}

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={salvar}
              disabled={disabled}
            >
              Salvar
            </Button>

            {' '}

            <Button
              onClick={onClickCancelar}
              disabled={disabled}
            >
              Cancelar
            </Button>
          </div>

          {message && (
            <Typography
              className={classes.message}
              color={error ? 'error' : 'primary'}
            >
              {message}
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
};